import { CheckCircle2, CircleOff, Plus, RefreshCw } from 'lucide-react'

import { ActionInfoQuiet, Button, ResourceTile, ResourceTileGrid, ScreenInfo } from '@krp/ui-kit'

import '../../../shared/platform.css'

import type { ResourcesActions, ResourcesVm } from '../model/types'
import { ActionReason } from './ActionReason'
import { QueryState } from '../../../shared/QueryState'

const SCREEN_INFO = {
  title: 'Что на этом экране',
  lines: [
    'Здесь все подключённые источники: базы 1С, кабинеты AQSI и собственная касса.',
    'Нажмите «Открыть» у источника — откроется его карточка: настройки, проверка связи и рабочие окна.',
    '«Добавить источник» подключает новую базу или кабинет: выберите вид и заполните форму.',
    'Выключенный источник остаётся в списке, но с ним никто не работает.',
  ],
} as const

/**
 * Вкладка «Источники»: простой список карточек подключённых источников без фильтров. Экран получает vm и actions и
 * больше ничего: ни store, ни HTTP, ни storage (D-053, R02). Состояние источника — значок и слово, а не цвет:
 * выключенные не прячутся, а приглушены и подписаны. Справка экрана одна, ⓘ сверху слева; у отдельных кнопок своих ⓘ нет.
 * Глобального «текущего источника» нет: каждый открывается своим окном с rid.
 */
export function ResourcesScreen({ vm, actions }: { vm: ResourcesVm; actions: ResourcesActions }) {
  return (
    <ActionInfoQuiet>
      <section aria-labelledby="platform-resources-title" className="platform-resources">
        <header className="platform-resources__toolbar">
          <div className="platform-resources__title">
            <ScreenInfo title={SCREEN_INFO.title} lines={SCREEN_INFO.lines} />
            <h2 id="platform-resources-title">Источники</h2>
          </div>
          <Button
            variant="ghost"
            icon={<RefreshCw size={16} aria-hidden="true" />}
            busy={vm.query.refreshing}
            onClick={actions.reload}
            actionId="platform.resources.reload"
          >
            Обновить
          </Button>
        </header>

        <div className="platform-resources__add">
          {vm.add.allowed ? (
            <Button
              variant="primary"
              icon={<Plus size={16} aria-hidden="true" />}
              onClick={actions.add}
              actionId="platform.resources.add"
            >
              Добавить источник
            </Button>
          ) : (
            <>
              <Button
                variant="secondary"
                icon={<Plus size={16} aria-hidden="true" />}
                disabled
                actionId="platform.resources.add-disabled"
                disabledReason={vm.add.reason}
              >
                Добавить источник
              </Button>
              <ActionReason action={vm.add} />
            </>
          )}
        </div>

        <QueryState query={vm.query} emptyText="Источники ещё не подключены" onRetry={actions.reload} />

        {vm.query.status === 'ready' && (
          <ResourceTileGrid label="Источники">
            {vm.rows.map((row) => (
              <ResourceTile
                key={row.id}
                mark={row.mark}
                noun={row.kindTitle}
                name={row.name}
                tone={row.tone}
                muted={!row.enabled}
                footer={
                  <>
                    <p className="platform-resources__state">
                      {row.enabled ? <CheckCircle2 size={16} aria-hidden="true" /> : <CircleOff size={16} aria-hidden="true" />}
                      {row.enabled ? 'включён' : 'выключен'}
                    </p>
                    <Button
                      variant={row.enabled ? 'primary' : 'secondary'}
                      onClick={() => actions.open(row.id)}
                      aria-label={`Открыть источник ${row.name}`}
                      actionId="platform.resources.open"
                    >
                      Открыть
                    </Button>
                  </>
                }
              />
            ))}
          </ResourceTileGrid>
        )}
      </section>
    </ActionInfoQuiet>
  )
}
